import { computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { I18nService } from '@/domain/services/i18n.service'
import { I18nRepository } from '@/infrastructure/repositories/i18n.repository'
import { saveLocalePreference } from '@/utils/locale'

const loadedLocales = new Set<string>()

/**
 * Опции для useLocaleSwitch
 */
interface UseLocaleSwitchOptions {
  onLocaleChange?: (oldLocale: string, newLocale: string) => void
  onRouteLocaleDetected?: (routeLocale: string) => void
  /** Колбек обновления роута (например, из useRouteLocale). При наличии changeLocale сначала вызывает его, затем setLocale. */
  updateRouteLocale?: (newLocale: string) => Promise<void>
}

/**
 * Проверяет наличие сообщений для локали: сначала в глобальном scope (defineI18nConfig),
 * затем в локальном scope useI18n(). Это критично потому что useI18n() в composable
 * возвращает локальный scope с пустыми messages, тогда как defineI18nConfig наполняет
 * глобальный scope — i18n.getLocaleMessage() на локальном scope всегда вернёт {}.
 *
 * nuxtApp передаётся явно: функция вызывается из async watch (после nextTick),
 * где Nuxt-контекст уже недоступен и useNuxtApp() выбросит ошибку.
 */
// Структурні типи параметрів: функція використовує лише getLocaleMessage / $i18n,
// тож не прив'язуємось до згенерованої i18n-схеми чи повного типу NuxtApp
// (інакше повна схема локалей / _NuxtApp не присвоюються вузьким дефолтним типам).
function hasLocaleMessages(
  localI18n: { getLocaleMessage: (locale: string) => unknown },
  locale: string,
  nuxtApp: { $i18n: { getLocaleMessage?: (locale: string) => unknown } },
): boolean {
  // Глобальный scope: nuxtApp.$i18n (там живут messages из defineI18nConfig)
  const globalMessages = (nuxtApp.$i18n as any).getLocaleMessage?.(locale) ?? {}
  if (Object.keys(globalMessages).length > 0) return true

  // Локальный scope: useI18n() (там живут messages, установленные через setLocaleMessage)
  const localMessages = localI18n.getLocaleMessage(locale)
  return Object.keys((localMessages ?? {}) as object).length > 0
}

/**
 * Composable для переключения локали с подгрузкой сообщений из public/i18n.
 * Роут является единственным источником истины для локали.
 */
export function useLocaleSwitch(options?: UseLocaleSwitchOptions) {
  const i18n = useI18n()
  const route = useRoute()
  // Захоплюємо під час Vue setup — щоб передавати в async-колбеки де контекст недоступний
  const nuxtApp = useNuxtApp()

  const repo = I18nRepository.getInstance()
  const service = I18nService.getInstance(repo)

  // Кешируем значения сервиса
  const supportedLocales = service.getSupportedLocales()
  const defaultLocale = service.getDefaultLocale()

  /**
   * Получает локаль из роута или возвращает дефолтную
   */
  const getCurrentLocale = (): string => {
    const routeLocale = route.params.locale as string

    if (routeLocale && supportedLocales.includes(routeLocale as any)) {
      return routeLocale
    }

    return defaultLocale
  }

  /**
   * Устанавливает локаль с загрузкой переводов (внутренний метод).
   * Проверяет глобальный scope (defineI18nConfig) перед тем как делать сетевой запрос —
   * en/uk/ru уже в бандле и не требуют fetch.
   */
  const setLocale = async (code: string) => {
    const oldLocale = i18n.locale.value

    if (!loadedLocales.has(code)) {
      if (hasLocaleMessages(i18n, code, nuxtApp)) {
        // Messages already present (global scope from defineI18nConfig or previously loaded)
        loadedLocales.add(code)
      } else {
        // Genuinely missing — fetch from /i18n/{locale}.json
        const messages = await service.loadLocale(code)
        i18n.setLocaleMessage(code, messages)
        loadedLocales.add(code)
      }
    }

    // Устанавливаем локаль в i18n
    ;(i18n.locale as { value: string }).value = code

    if (oldLocale !== code) {
      options?.onLocaleChange?.(oldLocale, code)
    }
  }

  /**
   * Главный метод для изменения локали.
   * Сначала выставляем i18n (язык интерфейса переключается сразу), затем по возможности обновляем роут.
   * Так выбранный язык всегда применяется даже если навигация не удалась (например, страница без [locale] в пути).
   */
  const changeLocale = async (newLocale: string) => {
    saveLocalePreference(newLocale)
    await setLocale(newLocale)
    if (options?.updateRouteLocale) {
      try {
        await options.updateRouteLocale(newLocale)
      } catch {
        // Игнорируем ошибку навигации — локаль интерфейса уже установлена
      }
    }
  }

  const currentLocale = computed(() => getCurrentLocale())

  // Единственный слушатель: синхронизируем i18n с локалью из роута.
  // При immediate: true откладываем первый вызов на nextTick, чтобы дочерние компоненты
  // (например LayoutHeaderLangSelector) успели смонтироваться и не падали с undefined handler при смене локали.
  //
  // Условие: вызываем setLocale если:
  //   a) локаль відрізняється від поточної i18n (перемикання мови), АБО
  //   b) messages для цієї локалі пусті (ні в глобальному, ні в локальному scope) —
  //      захист від ситуації коли messages ще не ініціалізовані.
  watch(() => route.params.locale, async (newRouteLocale: string | string[] | undefined) => {
    if (!newRouteLocale || !supportedLocales.includes(newRouteLocale as string)) return
    const locale = newRouteLocale as string
    const hasMsgs = hasLocaleMessages(i18n, locale, nuxtApp)
    if (locale !== i18n.locale.value || !hasMsgs) {
      await nextTick()
      await setLocale(locale)
      options?.onRouteLocaleDetected?.(locale)
    }
  }, { immediate: true })

  return {
    getCurrentLocale,
    currentLocale, // Реактивная текущая локаль
    changeLocale, // Главный метод для изменения локали (с обновлением роута)
    setLocale, // Внутренний метод для установки локали (без изменения роута)
    supportedLocales,
    defaultLocale,
  }
}
